import { useMutation, useQuery } from '@apollo/client';
import Build from '@clearkit/icons/glyphs/Build';
import { CKBox, CKButton, CKCardCollapsible } from '@clearkit/react';
import _ from 'lodash';
import capitalize from 'lodash/capitalize';
import { useMemo } from 'react';

import LoadingArea from '~/components/LoadingArea';
import SearchBuilder, { validateSearch } from '~/components/SearchBuilder';
import { EMPTY_STATE_SEARCH } from '~/components/SearchBuilder/utils/constants';
import ToastsContainer from '~/containers/Toasts';
import { readableType } from '~/lib/dataEnrichment';
import {
  getAttributeType,
  getEmptyStateSearch,
  getEmptyStateSubObject,
} from '~/lib/selectiveEnrichment';

import EmptyState from '../FieldMappingForm/EmptyState.svg';
import mutation from './mutation.graphql';
import query from './query.graphql';
import { ATTRIBUTE_TYPES_TO_REMOVE } from './utils';

const SelectiveEnrichmentConfig = ({ objectType, integration }) => {
  const salesforceSubCategories = [objectType.name];
  const attributeType = getAttributeType(integration, objectType);

  // Pull account connector w/ any previosly saved selective enrichment filters +
  // attributes + salesforce users to populate trait selector dropdown
  const {
    data: { searchAttributes: attributes, salesforceUsers, accountConnector },
    loading,
  } = useQuery(query, {
    returnPartialData: true,
    variables: {
      type: attributeType,
      externalObjectType: objectType.name,
      service: integration,
      collectionType: attributeType,
      salesforceSubCategories,
    },
  });

  const savedSelectiveEnrichmentConfig =
    accountConnector?.selectiveEnrichmentFilters?.filters;
  const [filters, setFilters] = useState(savedSelectiveEnrichmentConfig);
  const [showInvalidFields, setShowInvalidFields] = useState(false);
  const filtersAreValid = validateSearch(filters).length === 0;
  const input = useMemo(
    () => ({
      id: accountConnector?.id,
      externalObjectType: capitalize(objectType.name),
      filters,
    }),
    [accountConnector, filters, objectType],
  );

  const filteredAttributes = useMemo(
    () =>
      attributes?.filter(
        (attr) => !ATTRIBUTE_TYPES_TO_REMOVE.includes(attr?.details?.type),
      ),
    [objectType.name, attributes, filters],
  );

  const updateFilters = (fields) => {
    if (_.isEqual(fields.search, EMPTY_STATE_SEARCH)) {
      setFilters(getEmptyStateSearch(integration, objectType));
    } else {
      setFilters(fields.search);
    }
  };

  useEffect(() => {
    if (savedSelectiveEnrichmentConfig) {
      setFilters(savedSelectiveEnrichmentConfig);
    }
  }, [objectType.name, savedSelectiveEnrichmentConfig]);

  const [
    updateSelectiveEnrichmentConfig,
    { loading: isUpdating },
  ] = useMutation(mutation, {
    onCompleted: () => {
      ToastsContainer.addSuccess(
        `${readableType(integration)} ${capitalize(
          objectType.name,
        )} selective enrichment configuration saved!`,
      );
    },
    onError: () => {
      ToastsContainer.addError(
        'There was an error saving this selective enrichment config.',
      );
    },
    variables: {
      input,
    },
  });

  return (
    <LoadingArea loading={loading}>
      <div className="px-4">
        <CKCardCollapsible
          header={
            <SelectiveEnrichmentHeader
              integration={integration}
              objectType={objectType}
            />
          }>
          <SelectiveEnrichmentBody
            integration={integration}
            objectType={objectType}
            setFilters={setFilters}
            attributeType={attributeType}
            attributes={filteredAttributes}
            filters={filters}
            showInvalidFields={showInvalidFields}
            salesforceUsers={salesforceUsers}
            updateFilters={updateFilters}
          />
          <div className="sticky bottom-0 flex flex-row items-center pt-6 mt-auto bg-white border-t border-gray-200">
            <div className="flex flex-row items-center ml-auto">
              <div className="flex items-center space-x-3">
                <CKButton
                  onClick={() => setFilters(savedSelectiveEnrichmentConfig)}
                  isDisabled={!filters}
                  variant={CKButton.Variant.bold}
                  variantColor={CKButton.VariantColor.green}>
                  Cancel
                </CKButton>
                <SaveSelectiveEnrichmentConfigButton
                  loading={isUpdating}
                  filtersAreValid={filtersAreValid}
                  setShowInvalidFields={setShowInvalidFields}
                  updateSelectiveEnrichmentConfig={
                    updateSelectiveEnrichmentConfig
                  }
                  filters={filters}
                />
              </div>
            </div>
          </div>
        </CKCardCollapsible>
      </div>
    </LoadingArea>
  );
};

function SaveSelectiveEnrichmentConfigButton({
  filters,
  updateSelectiveEnrichmentConfig,
  filtersAreValid,
  setShowInvalidFields,
  loading,
}) {
  return (
    <div
      onMouseEnter={() => setShowInvalidFields(true)}
      onMouseLeave={() => setShowInvalidFields(false)}>
      <CKButton
        isDisabled={!filters || !filtersAreValid || loading}
        variant={CKButton.Variant.bold}
        dataTestId="saveSelectiveEnrichmentConfig"
        onClick={updateSelectiveEnrichmentConfig}
        variantColor={CKButton.VariantColor.green}>
        Save
      </CKButton>
    </div>
  );
}
function SelectiveEnrichmentHeader({ integration, objectType }) {
  return (
    <>
      <div
        className="grid grid-cols-[1fr,auto]"
        data-testid="selectiveEnrichmentConfigCardHeader">
        <div className="text-base">
          <h3 className="mb-2 font-medium">Selective Sync </h3>
          <p>
            Use selective sync to create an enrichment filter that will limit
            the sync of enriched data to your CRM to{' '}
            <span className="font-medium">
              {capitalize(integration)} {objectType.name}s that match criteria
              defined by you.
            </span>
          </p>
        </div>
      </div>
    </>
  );
}

function SelectiveEnrichmentBody({
  integration,
  objectType,
  setFilters,
  attributeType,
  attributes,
  salesforceUsers,
  filters,
  updateFilters,
  showInvalidFields,
}) {
  return !filters ? (
    <EmptyStateSelectiveEnrichment
      integration={integration}
      objectType={objectType}
      setFilters={setFilters}
    />
  ) : (
    <SearchBuilder
      type={attributeType}
      search={filters}
      attributes={attributes}
      isSelectiveEnrichmentConfig
      salesforceUsers={salesforceUsers}
      showInvalidFields={showInvalidFields}
      onChange={(search) => {
        updateFilters({ search });
      }}
      emptyGroup={getEmptyStateSubObject(integration, objectType)}
    />
  );
}

function EmptyStateSelectiveEnrichment({
  integration,
  objectType,
  setFilters,
}) {
  return (
    <CKBox
      className="flex flex-col items-center p-10 m-10"
      variant={CKBox.Variant['tinted-frame']}>
      <EmptyState />
      <p className="text-base font-medium text-center text-gray-800 mt-7">
        Get selective around sync
      </p>
      <p className="max-w-md mt-2 text-sm text-center text-gray-600">
        Before you can use selective sync for Salesforce {objectType.name}s,
        you’ll first need to define the criteria around which {objectType.name}s
        we should sync.
      </p>
      <div className="mt-6">
        <CKButton
          variant="bold"
          variantColor="blue"
          onClick={() => {
            setFilters(getEmptyStateSearch(integration, objectType));
          }}
          className="mr-4"
          leftIcon={<Build />}>
          Set up selective sync for Salesforce {objectType.name}s
        </CKButton>
      </div>
    </CKBox>
  );
}

export default SelectiveEnrichmentConfig;
